
import { Component, Prop, Watch } from "vue-property-decorator"
import { API_TYPE } from "~/constants/apiTypes"
import { Core } from "~/core/Core"
import { _debounce, _isEmpty, _isUndefined } from "~/utils/lodash"
import InfiniteLoading from "vue-infinite-loading"
import { PageInfiniteLoader } from "~/loader/pageInfinite"
import { PageLoader } from "~/loader/page"
import { Base } from "~/core/Base"
import { v4 as uuidV4 } from "uuid"

@Component({
  components: {
    InfiniteLoading,
  },
})
export default class ImageAssetDialog extends Base {
  @Prop() readonly inputID!: string
  @Prop({ default: false }) readonly value!: boolean
  @Prop() readonly image?: string

  dialog = this.value

  page = 1
  search = ""
  imageURL: string | undefined = ""
  selectedItem: any = {}
  uploadIdempotencyKey = ""
  progress = 0
  localImageURL = ""
  tempState: any = undefined
  onSearch!: (txt) => void
  pollingTimer: ReturnType<typeof setInterval> | undefined

  created() {
    this.galleryLoader.initial()
    this.imageLoader.initial()
    this.detailLoader.initial()
    this.onSearch = _debounce((txt) => {
      this.galleryLoader.fetchClear()
      this.page = 2
      this.galleryLoader.$fetch("", "", {
        params: {
          page: 1,
          q: txt,
          media_type: "image",
          sort: "newest",
        },
      })
    }, 300)

    this.$nuxt.$on(`on-upload-image-${this.inputID}`, (file) => {
      this.localImageURL = URL.createObjectURL(file)
      this.onUploadFile(file)
    })
  }

  mounted() {
    this.onSearch("")
    this.startPolling()
  }

  beforeDestroy() {
    this.$nuxt.$off(`on-upload-image-${this.inputID}`)
    if (!_isUndefined(this.tempState) && !_isEmpty(this.tempState)) {
      this.tempState?.reset()
    }
    this.galleryLoader.destroyLoader()
    this.imageLoader.destroyLoader()
    this.detailLoader.destroyLoader()
    this.stopPolling()
  }

  get galleries() {
    return this.galleryLoader.fetchItems || []
  }

  get total() {
    return this.galleryLoader.fetchOptions.totalItem || 0
  }

  async onClick(gal) {
    this.selectedItem = gal
    this.imageURL = this.galleryPreviewURL(gal)
    if (!gal.selectable) return
    await this.detailLoader.$get(gal.id)
    if (this.detailLoader.findStatus.isSuccess) {
      this.selectedItem = this.detailLoader.findItem
      this.imageURL = this.selectedURL
    }
  }

  get theme() {
    return {
      "--primaryLighter": this.$vuetify.theme.themes.light.primaryLighter,
    }
  }

  get isShowEmptyItem(): boolean {
    return !this.galleryLoader.fetchStatus.isLoading && _isEmpty(this.galleries)
  }

  displaySize(gal): string {
    let unit = "Bytes"
    let size: any = gal.metadata?.size || gal.size || 0
    if (size >= 1000000) {
      unit = "MB"
      size = (+size / 1000000).toFixed(2)
    } else if (size >= 10000) {
      unit = "KB"
      size = (+size / 1000).toFixed(2)
    }

    return `${size} ${unit}`
  }

  infiniteHandler($state) {
    this.galleryLoader.$fetch("", "", {
      params: { page: this.page++, q: this.search },
    })
    this.tempState = $state
  }

  onUploadFile(file) {
    let formData = new FormData()
    formData.append("file", file, file?.name)
    this.uploadIdempotencyKey = uuidV4()
    this.imageLoader.$add(formData, { onUploadProgress: this.onUploadProgress })
  }

  onUploadProgress(e: any) {
    this.progress = Math.round((100 * e.loaded) / e.total)
  }

  onSelectImage() {
    this.$emit("select", this.selectedURL || "")
  }

  get selectedURL(): string {
    return this.selectedItem.selected_url || this.selectedItem.original?.url || ""
  }

  galleryPreviewURL(gal): string {
    return gal.preview?.url || gal.selected_url || gal.original?.url || gal.url || ""
  }

  statusLabel(gal): string {
    if (gal.processing_health === "ABANDONED") return "Processing stopped"
    if (gal.processing_health === "DELAYED") return "Processing delayed"
    const labels = {
      QUEUED: "Waiting",
      ANALYZING: "Analyzing",
      PROCESSING: "Reducing",
      FINALIZING: "Finalizing",
      READY_OPTIMIZED: "Ready · Reduced",
      READY_ORIGINAL: "Ready · Original",
      BYPASSED: "Ready · Bypassed",
      FAILED: "Optimization failed",
    }
    return labels[gal.status] || gal.status || "Unknown"
  }

  statusColor(gal): string {
    if (gal.status === "FAILED" || gal.processing_health === "ABANDONED") {
      return "error"
    }
    if (gal.selectable) return "success"
    return "warning"
  }

  isProcessingAsset(gal): boolean {
    return ["QUEUED", "ANALYZING", "PROCESSING", "FINALIZING"].includes(
      gal.status
    )
  }

  async refreshAssets() {
    this.galleryLoader.fetchClear()
    this.page = 2
    await this.galleryLoader.$fetch("", "", {
      params: {
        page: 1,
        q: this.search,
        media_type: "image",
        sort: "newest",
      },
    })
  }

  get hasProcessingAssets(): boolean {
    return this.galleries.some((gallery) => this.isProcessingAsset(gallery))
  }

  startPolling() {
    this.stopPolling()
    this.pollingTimer = setInterval(() => {
      if (
        this.dialog &&
        this.hasProcessingAssets &&
        !this.galleryLoader.fetchStatus.isLoading
      ) {
        this.refreshAssets()
      }
    }, 5000)
  }

  stopPolling() {
    if (this.pollingTimer) clearInterval(this.pollingTimer)
    this.pollingTimer = undefined
  }

  onOpenBrowse() {
    document.getElementById(this.inputID)?.click()
  }

  get galleryLoader(): PageInfiniteLoader {
    return new PageInfiniteLoader(this, (data: any) => ({
      stateKey: "gallery",
      apiType: API_TYPE.MKT,
      baseURL: Core.MKTAPI("/media/assets"),
      params: {
        media_type: "image",
        sort: "newest",
      },

      onFetchSuccess: (_) => {
        if (!_isUndefined(this.tempState) && !_isEmpty(this.tempState)) {
          this.tempState?.loaded()
          if (this.galleries.length === this.total) {
            this.tempState?.complete()
          }
        }
      },
      onFetchError: (_) => {
        if (!_isUndefined(this.tempState) && !_isEmpty(this.tempState)) {
          this.tempState?.loaded()
        }
      },
    }))
  }

  get imageLoader(): PageLoader {
    return new PageLoader(this, (data: any) => ({
      stateKey: "image-upload",
      apiType: API_TYPE.MKT,
      baseURL: Core.MKTAPI("/media/assets"),
      baseAdd: Core.MKTAPI("/media/assets"),

      callback: {
        onUploadProgress: data?.options?.onUploadProgress,
      },
      headers: {
        "Content-Type": "multipart/form-data",
        "x-idempotency-key": this.uploadIdempotencyKey,
      },

      onAddSuccess: (_, addedData) => {
        this.selectedItem = addedData
        this.imageURL = this.selectedURL
        this.refreshAssets()
      },
    }))
  }

  get detailLoader(): PageLoader {
    return new PageLoader(this, () => ({
      stateKey: "media-asset-detail",
      apiType: API_TYPE.MKT,
      baseURL: Core.MKTAPI("/media/assets"),
    }))
  }

  @Watch("value")
  public onValueChanged(val: boolean) {
    this.dialog = val
  }

  @Watch("image")
  onUpdateImageURL() {
    this.imageURL = this.image
  }
}
